import React, {useState} from 'react';
import {Pressable, View, Text, Platform, PressableProps} from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  Easing,
  withSequence,
} from 'react-native-reanimated';
import Check from '../../assets/icons/check.svg';
import {useTheme} from '../../utils/ThemeContext';
import {ICheckboxProps} from '../../models/props';
import BaseText from '../BaseText';

const Checkbox: React.FC<ICheckboxProps & PressableProps> = ({
  id,
  checked,
  onCheckedChange,
  label,
  ...props
}) => {
  const {theme} = useTheme();

  // For web pop effect
  const [isScaled, setIsScaled] = useState(false);

  // Reanimated values for mobile
  const circleScale = useSharedValue(0);
  const checkboxScale = useSharedValue(1);

  const handlePress = () => {
    if (onCheckedChange) {
      onCheckedChange(!checked);
    }

    if (Platform.OS === 'web') {
      // Web-only scale effect
      setIsScaled(true);
      setTimeout(() => setIsScaled(false), 250); // Reset scale after a brief delay
    } else {
      // Mobile animation with reanimated
      circleScale.value = withSequence(
        withTiming(1.2, {duration: 200, easing: Easing.out(Easing.quad)}),
        withTiming(0, {duration: 200, easing: Easing.in(Easing.quad)}),
      );

      checkboxScale.value = withSequence(
        withTiming(1.3, {duration: 100, easing: Easing.out(Easing.quad)}),
        withTiming(1, {duration: 100, easing: Easing.in(Easing.quad)}),
      );
    }
  };

  // Animated styles for mobile
  const animatedCircleStyle = useAnimatedStyle(() => ({
    transform: [{scale: circleScale.value}],
  }));

  const animatedCheckboxStyle = useAnimatedStyle(() => ({
    transform: [{scale: checkboxScale.value}],
  }));

  return (
    <View className="flex-row items-center  gap-2">
      <Pressable {...props} onPress={handlePress} className="relative">
        {/* Background circle */}
        {Platform.OS === 'web' ? (
          <View
            className={`absolute -top-[8px] -left-[8px] w-10 h-10 rounded-full transition-transform duration-200 ${
              isScaled ? '' : 'scale-0'
            } ${checked ? 'bg-primary-100  ' : ''} dark:bg-primary-dark-100`}
          />
        ) : (
          <Animated.View
            style={animatedCircleStyle}
            className="absolute -top-[7px] -left-[7px] w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-dark-100"
          />
        )}

        {Platform.OS === 'web' ? (
          <View
            className={`w-6 h-6 rounded-lg flex items-center justify-center ${
              checked
                ? `bg-primary-500 ${
                    isScaled ? 'scale-110' : 'scale-100'
                  } transition-transform duration-100`
                : 'border border-neutral-400 dark:border-neutral-dark-700 scale-100 transition-transform duration-200'
            }`}>
            {checked && (
              <Check
                width={16}
                height={16}
                stroke={theme === 'dark' ? 'black' : 'white'}
              />
            )}
          </View>
        ) : (
          <Animated.View
            style={animatedCheckboxStyle}
            className={`w-6 h-6 rounded-lg flex items-center justify-center ${
              checked
                ? 'bg-primary-500'
                : 'bg-neutral-300 dark:bg-neutral-dark-300'
            }`}>
            {checked && (
              <Check
                width={16}
                height={16}
                stroke={theme === 'dark' ? 'black' : 'white'}
              />
            )}
          </Animated.View>
        )}
      </Pressable>
      {label && (
        <BaseText
          type="body2"
          className=" truncate max-w-[300px]"
          color={checked ? 'base' : 'secondary'}>
          {label}
        </BaseText>
      )}
    </View>
  );
};

export default Checkbox;
